import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '../contexts/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
    ArrowLeft, MapPin, Coffee, TreePine, Landmark, 
    ChevronRight, Star, ExternalLink, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function LocalAreaPage() {
    const navigate = useNavigate();
    const { organization } = useOrganization();
    const { token } = useAuth();
    const [activeTab, setActiveTab] = useState('highlights');
    const [highlights, setHighlights] = useState([]);
    const [cafesAndRestaurants, setCafesAndRestaurants] = useState([]);
    const [thisWeekPicks, setThisWeekPicks] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (token) {
            fetchLocalData();
        } else {
            setDefaults();
            setLoading(false);
        }
    }, [token]);

    const setDefaults = () => {
        // Check if this is Burston or Elysian for appropriate local area content
        const isBurston = organization?.slug === 'burston' || organization?.name?.toLowerCase().includes('burston');
        
        if (isBurston) {
            setHighlights([
                {
                    id: '1',
                    name: 'St Albans City Centre',
                    category: 'Shopping & Dining',
                    description: 'Historic market town with unique shops, cafés, and the famous twice-weekly market.',
                    distance: '5 min drive',
                    image_url: 'https://images.unsplash.com/photo-1763736810095-27f60b094dee?w=800&h=600&fit=crop',
                    is_featured: true,
                    website: 'https://www.enjoystalbans.com'
                },
                {
                    id: '2',
                    name: 'St Albans Cathedral',
                    category: 'Heritage',
                    description: 'One of the oldest cathedrals in England, with stunning Norman architecture and beautiful grounds.',
                    distance: '8 min drive',
                    image_url: 'https://images.unsplash.com/photo-1724340500642-984e02b8412d?w=800&h=600&fit=crop',
                    website: 'https://www.stalbanscathedral.org'
                },
                {
                    id: '3',
                    name: 'Verulamium Park',
                    category: 'Parks & Nature',
                    description: 'Beautiful 100-acre park with Roman ruins, ornamental lake, and lovely walks through woodland.',
                    distance: '10 min drive',
                    image_url: 'https://images.pexels.com/photos/14481615/pexels-photo-14481615.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop',
                },
                {
                    id: '4',
                    name: 'Burston Garden Centre',
                    category: 'Garden & Home',
                    description: 'Your local garden centre with plants, gifts, and a wonderful restaurant. Member benefits apply!',
                    distance: 'On-site',
                    image_url: 'https://images.unsplash.com/photo-1748267984369-76a1c4849df8?w=800&h=600&fit=crop',
                    is_featured: true,
                    website: 'https://www.burston.co.uk'
                },
            ]);
            setCafesAndRestaurants([
                { id: '1', name: 'The Waffle House', type: 'Café', speciality: 'Famous for Belgian waffles & all-day brunch', distance: '8 min drive', address: 'St Michaels Street, St Albans', website: 'https://wafflehouse.co.uk', image_url: 'https://images.unsplash.com/photo-1758648207365-df458d3e83f4?w=400&h=300&fit=crop' },
                { id: '2', name: 'Lussmanns', type: 'Restaurant', speciality: 'Sustainable fish & seasonal British classics', distance: '10 min drive', address: 'Waxhouse Gate, St Albans', website: 'https://lussmanns.com', image_url: 'https://images.unsplash.com/photo-1770910045510-77e24f37988f?w=400&h=300&fit=crop' },
                { id: '3', name: 'Ye Olde Fighting Cocks', type: 'Historic Pub', speciality: 'Britain\'s oldest pub, traditional food & ales', distance: '11 min drive', address: 'Abbey Mill Lane, St Albans', website: 'https://yeoldefightingcocks.co.uk', image_url: 'https://images.unsplash.com/photo-1659085096109-f25a38cb2466?w=400&h=300&fit=crop' },
                { id: '4', name: 'Burston Restaurant', type: 'Garden Café', speciality: 'Fresh home-cooked meals with garden views', distance: 'On-site', address: 'Burston Garden Centre', website: 'https://www.burston.co.uk/restaurant', image_url: 'https://images.unsplash.com/photo-1753729349067-f317d8a80b9f?w=400&h=300&fit=crop' },
            ]);
        } else {
            // Elysian - Same St Albans local highlights
            setHighlights([
                {
                    id: '1',
                    name: 'St Albans City Centre',
                    category: 'Shopping & Dining',
                    description: 'Historic market town with unique shops, cafés, and the famous twice-weekly market.',
                    distance: '10 min drive',
                    image_url: 'https://images.unsplash.com/photo-1763736810095-27f60b094dee?w=800&h=600&fit=crop',
                    is_featured: true,
                    website: 'https://www.enjoystalbans.com'
                },
                {
                    id: '2',
                    name: 'St Albans Cathedral',
                    category: 'Heritage',
                    description: 'One of the oldest cathedrals in England, with stunning Norman architecture and beautiful grounds.',
                    distance: '12 min drive',
                    image_url: 'https://images.unsplash.com/photo-1724340500642-984e02b8412d?w=800&h=600&fit=crop',
                    website: 'https://www.stalbanscathedral.org'
                },
                {
                    id: '3',
                    name: 'Verulamium Park',
                    category: 'Parks & Nature',
                    description: 'Beautiful 100-acre park with Roman ruins, ornamental lake, and lovely walks through woodland.',
                    distance: '12 min drive',
                    image_url: 'https://images.pexels.com/photos/14481615/pexels-photo-14481615.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop',
                },
                {
                    id: '4',
                    name: 'Burston Garden Centre',
                    category: 'Garden & Home',
                    description: 'Award-winning garden centre with plants, gifts, café, and seasonal events.',
                    distance: '8 min drive',
                    image_url: 'https://images.unsplash.com/photo-1748267984369-76a1c4849df8?w=800&h=600&fit=crop',
                    is_featured: true,
                    website: 'https://www.burston.co.uk'
                },
            ]);
            setCafesAndRestaurants([
                { id: '1', name: 'The Waffle House', type: 'Café', speciality: 'Famous for Belgian waffles & all-day brunch', distance: '12 min drive', address: 'St Michaels Street, St Albans', website: 'https://wafflehouse.co.uk', image_url: 'https://images.unsplash.com/photo-1758648207365-df458d3e83f4?w=400&h=300&fit=crop' },
                { id: '2', name: 'Lussmanns', type: 'Restaurant', speciality: 'Sustainable fish & seasonal British classics', distance: '12 min drive', address: 'Waxhouse Gate, St Albans', website: 'https://lussmanns.com', image_url: 'https://images.unsplash.com/photo-1770910045510-77e24f37988f?w=400&h=300&fit=crop' },
                { id: '3', name: 'Ye Olde Fighting Cocks', type: 'Historic Pub', speciality: 'Britain\'s oldest pub, traditional food & ales', distance: '12 min drive', address: 'Abbey Mill Lane, St Albans', website: 'https://yeoldefightingcocks.co.uk', image_url: 'https://images.unsplash.com/photo-1659085096109-f25a38cb2466?w=400&h=300&fit=crop' },
                { id: '4', name: 'Burston Restaurant', type: 'Garden Café', speciality: 'Fresh home-cooked meals with garden views', distance: '8 min drive', address: 'Burston Garden Centre', website: 'https://www.burston.co.uk/restaurant', image_url: 'https://images.unsplash.com/photo-1753729349067-f317d8a80b9f?w=400&h=300&fit=crop' },
            ]);
        }
        
        // Organization-specific weekly picks
        if (isBurston) {
            setThisWeekPicks([
                {
                    id: '1',
                    title: 'St Albans Farmers Market',
                    description: 'Fresh local produce, artisan cheeses, and homemade treats',
                    when: 'Every Saturday, 8am-2pm',
                },
                {
                    id: '2',
                    title: 'Burston Spring Plant Fair',
                    description: 'Special offers on bedding plants and expert gardening advice',
                    when: 'This Weekend',
                },
                {
                    id: '3',
                    title: 'Cathedral Tours',
                    description: 'Free guided tours of St Albans Cathedral',
                    when: 'Wednesdays & Saturdays, 11am',
                },
            ]);
        } else {
            setThisWeekPicks([
                {
                    id: '1',
                    title: 'St Albans Farmers Market',
                    description: 'Fresh local produce, artisan cheeses, and homemade treats',
                    when: 'Every Saturday, 8am-2pm',
                },
                {
                    id: '2',
                    title: 'Cathedral Tours',
                    description: 'Free guided tours of St Albans Cathedral',
                    when: 'Wednesdays & Saturdays, 11am',
                },
                {
                    id: '3',
                    title: 'Verulamium Museum Visit',
                    description: 'Discover Roman St Albans with expert guides',
                    when: 'Thursdays, 2pm',
                },
            ]);
        }
    };

    const fetchLocalData = async () => {
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const [placesRes, diningRes, picksRes] = await Promise.all([
                axios.get(`${API}/local/places`, { headers }),
                axios.get(`${API}/local/dining-spots`, { headers }),
                axios.get(`${API}/local/weekly-picks`, { headers }),
            ]);
            setHighlights(placesRes.data);
            setCafesAndRestaurants(diningRes.data);
            setThisWeekPicks(picksRes.data);
        } catch (error) {
            console.error('Error fetching local data:', error);
            setDefaults();
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-stone-50 pb-24" data-testid="local-page">
            {/* Header */}
            <div className="bg-gradient-to-br from-teal-700 to-teal-800 text-white px-6 pt-6 pb-8">
                <button 
                    onClick={() => navigate('/home')}
                    className="flex items-center gap-2 text-teal-200 hover:text-white mb-4"
                >
                    <ArrowLeft className="w-5 h-5" />
                    Home
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                        <MapPin className="w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-serif text-white">Local Area</h1>
                        <p className="text-white/80 text-sm">Around {organization?.name || 'The Oakleigh'}</p>
                    </div>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="px-4 -mt-4 relative z-10">
                <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-1 flex">
                    {['highlights', 'dining', 'this week'].map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`flex-1 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${
                                activeTab === tab
                                    ? 'bg-teal-600 text-white'
                                    : 'text-stone-600 hover:bg-stone-100'
                            }`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>
            </div>

            {/* Content */}
            <div className="px-4 mt-6">
                {/* Highlights Tab */}
                {activeTab === 'highlights' && (
                    <div className="space-y-4">
                        <h2 className="text-lg font-semibold text-stone-800">Local Highlights</h2>
                        
                        {highlights.map((place) => (
                            <div 
                                key={place.id} 
                                className={`bg-white rounded-2xl overflow-hidden border ${
                                    place.is_featured ? 'border-teal-200 ring-2 ring-teal-100' : 'border-stone-200'
                                }`}
                            >
                                <div 
                                    className="h-32 bg-cover bg-center relative"
                                    style={{ backgroundImage: `url(${place.image_url})` }}
                                >
                                    {place.is_featured && (
                                        <div className="absolute top-3 left-3 bg-teal-600 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                                            <Star className="w-3 h-3" />
                                            Recommended
                                        </div>
                                    )}
                                </div>
                                <div className="p-4">
                                    <span className="text-xs text-teal-600 font-medium">{place.category}</span>
                                    <h3 className="font-semibold text-stone-800 mt-1">{place.name}</h3>
                                    <p className="text-sm text-stone-500 mt-1">{place.description}</p>
                                    <div className="flex items-center justify-between mt-3">
                                        <span className="text-xs text-stone-400 flex items-center gap-1">
                                            <MapPin className="w-3 h-3" />
                                            {place.distance}
                                        </span>
                                        <Button variant="ghost" size="sm" className="text-teal-600">
                                            <ExternalLink className="w-4 h-4 mr-1" />
                                            Directions
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Dining Tab */}
                {activeTab === 'dining' && (
                    <div className="space-y-4">
                        <h2 className="text-lg font-semibold text-stone-800">Cafés & Restaurants</h2>
                        
                        {cafesAndRestaurants.map((place) => (
                            <button 
                                key={place.id} 
                                onClick={() => place.website && window.open(place.website, '_blank')}
                                className={`w-full bg-white rounded-2xl p-4 border border-stone-200 flex items-center gap-4 text-left transition-all ${place.website ? 'hover:shadow-md hover:border-teal-300 cursor-pointer' : 'cursor-default'}`}
                            >
                                <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center flex-shrink-0">
                                    <Coffee className="w-6 h-6 text-teal-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <h3 className="font-semibold text-stone-800">{place.name}</h3>
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">
                                            {place.type}
                                        </span>
                                    </div>
                                    <p className="text-sm text-stone-500">{place.speciality}</p>
                                    {place.address && (
                                        <p className="text-xs text-stone-400 mt-1">{place.address}</p>
                                    )}
                                    <span className="text-xs text-teal-600">{place.distance}</span>
                                </div>
                                {place.website ? (
                                    <ExternalLink className="w-5 h-5 text-teal-500" />
                                ) : (
                                    <ChevronRight className="w-5 h-5 text-stone-400" />
                                )}
                            </button>
                        ))}

                        <div className="bg-teal-50 rounded-2xl p-5 border border-teal-200">
                            <h3 className="font-semibold text-teal-800 mb-2">Resident Recommendations</h3>
                            <p className="text-teal-700 text-sm">
                                Have a favourite local spot? Share it with your neighbours through the Community section.
                            </p>
                        </div>
                    </div>
                )}

                {/* This Week Tab */}
                {activeTab === 'this week' && (
                    <div className="space-y-4">
                        <h2 className="text-lg font-semibold text-stone-800">This Week Around You</h2>
                        <p className="text-stone-500 text-sm">Curated picks for what's happening nearby.</p>
                        
                        {thisWeekPicks.map((pick) => (
                            <div key={pick.id} className="bg-white rounded-2xl p-5 border border-stone-200">
                                <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                                        <Star className="w-5 h-5 text-amber-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-stone-800">{pick.title}</h3>
                                        <p className="text-sm text-stone-500 mt-1">{pick.description}</p>
                                        <div className="flex items-center gap-1 mt-2 text-xs text-teal-600">
                                            <Clock className="w-3 h-3" />
                                            {pick.when}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                        <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-5 text-white">
                            <h3 className="font-semibold mb-2">Partner Experiences</h3>
                            <p className="text-teal-100 text-sm mb-4">
                                Exclusive events and sessions arranged with Burston Garden Centre and local partners.
                            </p>
                            <Button variant="secondary" size="sm" className="bg-white text-teal-700 hover:bg-teal-50">
                                View Partner Events
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default LocalAreaPage;
